import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SxProp, View } from 'dripsy';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { LayoutContext } from 'contexts/LayoutContext';
import { EarnStackParamList, EarnStackRoutes } from '../..';
import { SubscribeStackRoutes, SubscribeStackScreenProps } from './type';
import { CurrenciesIcon } from '../../components';
import { CurrencyIconVariant } from '../../components/CurrenciesIcon';
import { t } from 'i18next';
import { ProgressStatus, UniProgressCard } from '@/design-system/components/progress';
import { UniCheckbox } from '@/design-system/components/inputs';
import { useStore } from 'store/store';
import { UniTextDetailsCard } from '@/design-system/components/cards';
import { BottomSheetHeader } from '@/design-system/components/headers';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CurrenciesContext } from '@/currencies/currency.context';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { AmountSummary } from '../../components/cards/AmountSummary';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { UserEarnDetailResponse } from '@utx/utx-client';
import { UserSubscriptionPreviewResponse } from '../../../../../../packages/utx-client/src/client';
import { useExecuteSubscription } from './useExecuteSubscription.hook';

export const SubscribeConfirm: React.FC<SubscribeStackScreenProps<SubscribeStackRoutes.Confirm>> = ({ navigation, route }) => {
  const {
    offer,
    account,
    preview,
    walletId,
    progressCardData,
    textDetailsCardData,
    subscriptionAmount,
    estimatedRewardsAfterMaturity,
    asset,
    autoRenew,
    onLayout
  } = route.params;
  const layout = useContext(LayoutContext);
  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();

  // Hooks
  const bottomSheetRef = useRef<BottomSheet>(null);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  useEffect(() => {
    setIsAcknowledged(false);
    return () => {
      setIsAcknowledged(false);
    };
  }, []);

  const executeSubsciption = useExecuteSubscription({
    requestParams: {
      offerId: offer.id || '',
      walletId: walletId || '',
      amount: subscriptionAmount,
      asset,
      autoRenew
    },
    assetDetails: offer,
    preview,
    account,
    onLayout
  });

  if (layout.isMobile) {
    // Get dimensions
    const renderBackdrop = useCallback(
      (props) => (
        <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />
      ),
      []
    );
    const { width, height } = layout.dimensions;
    const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
      'CONTENT_HEIGHT',
      'CONTENT_HEIGHT'
    ]);
    // Bottom Sheet on mobile
    return (
      <View sx={{ width, height, backgroundColor: 'transparent' }}>
        <BottomSheet
          index={0}
          ref={bottomSheetRef}
          snapPoints={animatedSnapPoints}
          handleHeight={animatedHandleHeight}
          contentHeight={animatedContentHeight}
          backdropComponent={renderBackdrop}
          handleComponent={() => <View sx={styles.bottomsheetHeadHandler} />}>
          <View sx={{ height: 'auto', width, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <BottomSheetHeader
              title={t('earn-invest::subscription-confirmation.header--subscription-confirmation')}
              onPress={() => {
                bottomSheetRef.current?.close();
                setTimeout(() => {
                  navigation.goBack();
                }, 300);
              }}
              sx={{ paddingX: '$4', pb: 0 }}
            />
            <View sx={{ padding: '$4', gap: '$4' }}>
              <Body
                offer={offer}
                preview={preview}
                asset={asset}
                isMobile={layout.isMobile}
                progressCardData={progressCardData}
                textDetailsCardData={textDetailsCardData}
                subscriptionAmount={subscriptionAmount}
                interest={estimatedRewardsAfterMaturity}
                loading={false}
                isAcknowledged={isAcknowledged}
                onAcknowledge={(value) => setIsAcknowledged(value)}
              />
              {executeSubsciption.errorText ? <UniAlert type={AlertType.Error} message={executeSubsciption.errorText} /> : null}
            </View>
            <UniButton
              onPress={executeSubsciption.execute}
              label={t('earn-invest::glossary.place-instruction')}
              wrapperSx={{ margin: '$5' }}
              size="default"
              disabled={!isAcknowledged}
              loading={executeSubsciption.isLoading}
            />
          </View>
        </BottomSheet>
      </View>
    );
  }

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={onLayout}>
      <Header title={offer.name} isMobile={layout.isMobile} onClose={() => earnNavigation.navigate(EarnStackRoutes.Main)} />
      <View sx={{ padding: '$7', gap: '$7' }}>
        <Body
          offer={offer}
          preview={preview}
          asset={asset}
          isMobile={layout.isMobile}
          progressCardData={progressCardData}
          textDetailsCardData={textDetailsCardData}
          subscriptionAmount={subscriptionAmount}
          interest={estimatedRewardsAfterMaturity}
          loading={false}
          isAcknowledged={isAcknowledged}
          onAcknowledge={(value) => setIsAcknowledged(value)}
        />
        {executeSubsciption.errorText ? <UniAlert type={AlertType.Error} message={executeSubsciption.errorText} /> : null}
      </View>
      <View sx={{ paddingX: '$7', pb: '$7', flexDirection: 'row', justifyContent: 'space-between', gap: '$2' }}>
        {!layout.isMobile && (
          <UniButton
            size="default"
            variant={ButtonVariant.outline1}
            onPress={() => navigation.goBack()}
            label={t('earn-invest::glossary.back')}
            wrapperSx={{ flex: 1 }}
          />
        )}
        <UniButton
          size="default"
          onPress={executeSubsciption.execute}
          label={t('earn-invest::glossary.place-instruction')}
          wrapperSx={{ flex: 1 }}
          disabled={!isAcknowledged}
          loading={executeSubsciption.isLoading}
        />
      </View>
    </View>
  );
};

const Header = ({ title, isMobile, onClose }: { title: string; isMobile: boolean; onClose: () => void }) => {
  if (isMobile) {
  }

  return <ModalHeader title={title} closeModal={onClose} />;
};

const Body = ({
  offer,
  preview,
  asset,
  interest,
  isMobile,
  isAcknowledged,
  progressCardData,
  textDetailsCardData,
  subscriptionAmount,
  loading,
  onAcknowledge
}: {
  offer: UserEarnDetailResponse;
  preview: UserSubscriptionPreviewResponse;
  asset: string;
  interest: number;
  isMobile: boolean;
  isAcknowledged: boolean;
  progressCardData: Array<ProgressStatus>;
  textDetailsCardData: Array<{
    name: string;
    subText: string;
    data: string;
  }>;
  subscriptionAmount: string;
  loading: boolean;
  onAcknowledge: (value: boolean) => void;
}) => {
  const store = useStore();
  const { currenciesDict } = useContext(CurrenciesContext);
  const tenor = getEarnTenorTranslationKey(offer.tenor);
  // const proceeds = new BigNumber(subscriptionAmount).plus(preview.estimatedRewardsAfterMaturity || 0).toNumber();
  const proceeds = preview.proceeds;

  return (
    <View sx={{ gap: '$5' }}>
      <CurrenciesIcon
        asset={asset}
        variant={isMobile ? CurrencyIconVariant.native : CurrencyIconVariant.default}
        textSize={isMobile ? 'textTitle' : 'H6'}
        sx={{ mt: '$1' }}
      />
      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <AmountSummary
          label={t('earn-invest::glossary.subscription-amount')}
          value={subscriptionAmount}
          asset={offer.asset}
          loading={loading}
          amountSx={{ color: '$secondary' }}
        />
        <AmountSummary
          label={t('earn-invest::glossary.est-apy')}
          value={offer.estimatedApy.toString()}
          asset="%"
          loading={loading}
          amountSx={{ color: '$textPrimary' }}
          alignRight
        />
      </View>
      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {offer.tenor ? (
          <AmountSummary
            label={t('earn-invest::glossary.interests')}
            value={formatAmountByDecimals({
              value: preview.estimatedRewardsAfterMaturity,
              decimals: currenciesDict?.[offer.asset]?.decimals
            })}
            asset={offer.asset}
            loading={loading}
            amountSx={{ color: '$success' }}
            success
          />
        ) : (
          <View />
        )}
        <AmountSummary
          label={t('earn-invest::glossary.tenor')}
          value={tenor.key ? t(tenor.key, tenor.options) : ''}
          asset=""
          amountSx={{ color: '$textPrimary' }}
          loading={loading}
          alignRight
        />
      </View>
      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {offer.tenor ? (
          <AmountSummary
            label={t('earn-invest::glossary.proceeds-at-maturity')}
            value={preview.proceeds}
            asset={offer.asset}
            loading={loading}
            amountSx={{ color: '$secondary' }}
            success
          />
        ) : (
          <View />
        )}
      </View>
      {textDetailsCardData.length > 0 ? <UniTextDetailsCard data={textDetailsCardData} /> : null}
      <UniProgressCard
        statusData={progressCardData}
        sx={{ paddingX: '$4', paddingY: '$3', backgroundColor: '$backgroundSecondary', borderRadius: 10 }}
      />
      <UniCheckbox
        label={t('earn-invest::subscription-confirmation.text--subscription-statement', { vendor: store.brand })}
        value={isAcknowledged}
        onValueChange={onAcknowledge}
        options={{
          labelSecondaryColor: true
        }}
        sx={{}}
        disabled={loading}
      />
    </View>
  );
};

enum StyleName {
  bottomsheetHeadHandler = 'bottomsheetHeadHandler'
}

const styles: Record<StyleName, SxProp> = {
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  }
};
